import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import { Types } from '@shared/all/domains/spaces';
import type { inferRouterOutputs } from '@trpc/server';

import type { AppRouter } from '../../../../apps/server/src/router';

type RouterOutputs = inferRouterOutputs<AppRouter>;

type FactiiiSearchReturnDTO =
  RouterOutputs['search']['factiiis']['factiiis'][0];

export type CreatedFactiii = RouterOutputs['factiiis']['add'];

type FactiiiRule = { id: number; title: string; description: string };

/**
 * A reducer rather than two dozen useStates because a template pick rewrites
 * most of this at once. The requirement fields mirror how the enum behaves:
 * `dataRequirement` and `locationRequirement` are single-choice groups (a tag
 * carries one value and captures one kind of place), `needsTime` is
 * independent of both, and everything else is a plain checkbox set.
 */
export type AddFactiiiState = {
  name: string;
  description: string;
  spaceSlug: string;
  isPrivate: boolean;

  dataRequirement: FactiiiRequirement | null;
  locationRequirement: FactiiiRequirement | null;
  needsTime: boolean;
  extraRequirements: FactiiiRequirement[];

  createAsGroup: boolean;
  groupFactiiiIds: number[];

  botEnabled: boolean;
  modelId: number | null;
  botPrompt: string;
  maxOutputTokens: number;
  /** 0–100, which is what `factiiis.add` expects — the server divides by 100. */
  temperature: number;
  taggableFactiiis: FactiiiSearchReturnDTO[];
  canTagMoreThanOne: boolean;

  rules: FactiiiRule[];
};

export const MAX_FACTIII_NAME_LENGTH = 21;

export const initialFormState = (
  name: string,
  spaceSlug: string
): AddFactiiiState => ({
  name,
  description: '',
  spaceSlug,
  isPrivate: false,
  dataRequirement: null,
  locationRequirement: null,
  needsTime: false,
  extraRequirements: [],
  createAsGroup: false,
  groupFactiiiIds: [],
  botEnabled: false,
  modelId: null,
  botPrompt: '',
  maxOutputTokens: 150,
  temperature: 70,
  taggableFactiiis: [],
  canTagMoreThanOne: false,
  rules: []
});

export type AddFactiiiAction =
  | { type: 'patch'; patch: Partial<AddFactiiiState> }
  | { type: 'toggleExtra'; requirement: FactiiiRequirement }
  | { type: 'addRule'; rule: FactiiiRule }
  | { type: 'removeRule'; id: number };

export const addFactiiiReducer = (
  state: AddFactiiiState,
  action: AddFactiiiAction
): AddFactiiiState => {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.patch };
    case 'toggleExtra':
      return {
        ...state,
        extraRequirements: state.extraRequirements.includes(action.requirement)
          ? state.extraRequirements.filter((r) => r !== action.requirement)
          : [...state.extraRequirements, action.requirement]
      };
    case 'addRule':
      return { ...state, rules: [...state.rules, action.rule] };
    case 'removeRule':
      return {
        ...state,
        rules: state.rules.filter((rule) => rule.id !== action.id)
      };
  }
};

/** The flat requirement list the server actually stores, de-duplicated. */
export const collectRequirements = (
  state: AddFactiiiState,
  forced: FactiiiRequirement[] = []
): FactiiiRequirement[] => [
  ...new Set([
    ...forced,
    ...state.extraRequirements,
    ...(state.dataRequirement ? [state.dataRequirement] : []),
    ...(state.locationRequirement ? [state.locationRequirement] : []),
    ...(state.needsTime ? [FactiiiRequirement.TIME_REQUIRED] : [])
  ])
];

/**
 * A ROOT factiii whose description is empty is inert: the description IS the
 * rule handed to the file tagger, so there is nothing for it to apply.
 */
export const rootNeedsRule = (
  state: AddFactiiiState,
  requirements: FactiiiRequirement[]
) =>
  requirements.includes(FactiiiRequirement.ROOT) && !state.description.trim();

/**
 * The exact `factiiis.add` payload. An AI reply with no model chosen, or a
 * group with nobody in it, is simply left off rather than blocking Create —
 * every setting in Options is optional, including the ones you half-filled.
 */
export const toAddInput = (
  state: AddFactiiiState,
  requirements: FactiiiRequirement[]
) => ({
  factiiiName: state.name,
  ...(state.description.trim()
    ? { description: state.description.trim() }
    : {}),
  types: state.isPrivate ? [Types.PRIVATE] : [],
  spaceSlug: state.spaceSlug,
  factiiiRequirements: requirements,
  replyConfig:
    state.botEnabled && state.modelId
      ? {
          modelId: state.modelId,
          maxOutputTokens: state.maxOutputTokens,
          temperature: state.temperature,
          prompt: state.botPrompt || undefined,
          taggableFactiiis: state.taggableFactiiis.length
            ? state.taggableFactiiis.map((factiii) => factiii.id)
            : undefined,
          canTagMoreThanOne: state.canTagMoreThanOne
        }
      : undefined,
  factiiiIds:
    state.createAsGroup && state.groupFactiiiIds.length
      ? state.groupFactiiiIds
      : undefined,
  rules: state.rules.length
    ? state.rules.map(({ title, description }) => ({ title, description }))
    : undefined
});
